import { useRef, useState } from "react";
import { Upload, Loader2, Music } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Click-to-upload MP3 picker for the radio admin panel.
 *
 * Props:
 *   onUploaded({ url, filename, size }) — required
 *   label  – button text (default "Upload MP3")
 *   testid – data-testid prefix
 */
export default function AudioUploader({ onUploaded, label = "Upload MP3", testid = "audio-uploader" }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    const isMp3 = /audio\/(mpeg|mp3|x-mpeg)/i.test(file.type) || /\.mp3$/i.test(file.name);
    if (!isMp3) {
      toast.error("Only MP3 audio files are accepted.");
      return;
    }
    if (file.size > 40 * 1024 * 1024) {
      toast.error("Audio is larger than 40MB.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setProgress(0);
    try {
      const res = await api.post("/upload/audio", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success(`Uploaded ${res.data.filename}`);
      onUploaded?.(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onPick = (e) => upload(e.target.files?.[0]);

  return (
    <>
      <button
        type="button"
        className="gold-btn"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`${testid}-btn`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Music className="h-4 w-4" />}
        {busy ? `Uploading ${progress}%` : label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="audio/mpeg,audio/mp3,.mp3"
        className="hidden"
        onChange={onPick}
        data-testid={`${testid}-input`}
      />
    </>
  );
}
